import type { CheckInBlock, UpcomingVisit } from '@/api/types/ProviderApi'
import { clockAt } from '@/features/visits/studioClock'

/**
 * What the slot says in each of the four states of arriving.
 *
 * The button is "I'm Here" wherever it can be pressed, which is the thing the member is telling
 * us — not "Check In", which described a mechanism and then needed a second line explaining what
 * it would do for them. Where it cannot be pressed yet the label is the moment it will wake up,
 * because a dead control naming its own hour needs no sentence under it. It
 * does nothing for them any more: the building's pass arrives by email on WeWork's schedule and
 * has a row of its own, so pressing this only lets the studio know somebody is in the waiting
 * room. One sentence where one is owed, none where the label is already the whole sentence.
 *
 * The moment is named rather than counted, from the server's own `opensAt` on the studio's wall
 * clock. "Fifteen minutes before your visit" is a subtraction a member has to perform against a
 * time they have to remember; a quarter past is a time they can wait for. Nothing here knows the
 * window is fifteen minutes.
 *
 * No `EXPIRED` branch: a shut window is `visitSlotState`'s own `closed` kind, which offers the
 * chat rather than a button that cannot be pressed.
 */
export const arrivalCopy = (
	visit: UpcomingVisit,
	checkIn: Pick<CheckInBlock, 'status' | 'opensAt'>
): { label: string; enabled: boolean; helper?: string } => {
	const label = "I'm Here"

	switch (checkIn.status) {
		case 'NOT_YET_OPEN':
			// The label names the moment the door opens, and nothing is said under it. A control
			// that will not move is the first place a member looks for the reason, and the line
			// beneath it was the second — so the sentence moved onto the thing it is about.
			return {
				label: `Check-in opens at ${clockAt(visit, checkIn.opensAt)}`,
				enabled: false,
			}
		case 'ENABLED':
			// Nothing owed. The label is the whole sentence, and on the page the row above it has
			// gone for the same reason: there is one thing to do and it is in front of them.
			return { label, enabled: true }
		default:
			// Checked in and expired never reach here — the slot draws a receipt for one and the
			// chat for the other — so there is nothing left to label.
			return { label, enabled: false }
	}
}

/**
 * An hour's margin before the door opens.
 *
 * Measured from the server's own opening instant, so nothing here knows the window is thirty
 * minutes. Long enough to cover someone who arrives early, short enough that the button does not
 * haunt the screen.
 */
const SHOW_BEFORE_OPEN_MS = 60 * 60 * 1000

/**
 * Whether the visit is close enough for the door to be worth drawing at all.
 *
 * The web page only exists inside the window, so it never had to decide this. A screen that
 * lives three weeks out does: a check-in button under a visit in October is a control that
 * cannot be used for a month, sitting above the two that can.
 */
export const shouldShowCheckIn = (checkIn: CheckInBlock, now: Date) => {
	// Both of the settled states still draw. Checked in, because the pass is on the panel and it
	// is the only thing a member wants while they wait. Closed, because the server now keeps a
	// visit until its scheduled end, so a member who arrives late still has it in front of them —
	// and "Check-in closed" tells them to speak to the desk, where an empty space tells them
	// nothing. Before the visit lasted that long this branch was unreachable and returned false.
	if (checkIn.status === 'CHECKED_IN' || checkIn.status === 'EXPIRED') {
		return true
	}
	return now.getTime() >= Date.parse(checkIn.opensAt) - SHOW_BEFORE_OPEN_MS
}

/**
 * How often to look again, or `false` where there is no point looking.
 *
 * The web page polls every thirty seconds for its whole life, which it can afford because it
 * only exists on the day. The same interval here would run against a visit three weeks out. So
 * it polls only once the door is nearly open, and stops once the answer can no longer change —
 * letting the server's own edges decide which of those is true rather than counting minutes.
 */
export const checkInPollMs = (checkIn: CheckInBlock | undefined, now: Date) => {
	if (!checkIn || checkIn.status === 'CHECKED_IN' || checkIn.status === 'EXPIRED') {
		return false as const
	}
	return shouldShowCheckIn(checkIn, now) ? 30_000 : (false as const)
}

/**
 * Whether the visit is the one happening now, rather than one that is merely next.
 *
 * The same rule that decides whether to draw the door, and deliberately so: if a member is close
 * enough to be offered check-in, the card should have stopped calling it their next visit. Two
 * thresholds here meant the page offered a door while Home still said "Next visit".
 */
export const isLive = (visit: UpcomingVisit, now: Date) =>
	!!visit.checkIn && shouldShowCheckIn(visit.checkIn, now)

/**
 * "in 25 mins", or "now" once the hour has come.
 *
 * Clamped, because a countdown that goes negative is telling a member their appointment happened
 * minus twelve minutes ago. Past the hour the honest answer is that they are due — the studio
 * runs late, and the app has no idea by how much.
 */
export const untilLabel = (startTimeUtc: string, now: Date) => {
	const minutes = Math.round((Date.parse(startTimeUtc) - now.getTime()) / 60_000)

	if (minutes <= 0) {
		return "You're up"
	}
	if (minutes < 60) {
		return `in ${minutes} min${minutes === 1 ? '' : 's'}`
	}
	const hours = Math.round(minutes / 60)
	return `in ${hours} hr${hours === 1 ? '' : 's'}`
}
